import { trechoDaSessao } from "./sessao";
/**
 * Integração entre os sistemas SERCAMP — navegação com login único (SSO via URL).
 * Os apps rodam localmente em portas diferentes; ao trocar de sistema levamos o
 * usuário logado pela URL (#sso=...) para não pedir login de novo.
 */

/** URL do Portal: localhost em dev; apex do dominio em producao (sem hardcode do dominio). */
function portalHost(): string {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    const isLocal = h === "localhost" || h === "127.0.0.1" || h === "::1";
    if (!isLocal) {
      // O Portal fica no apex; este sistema esta num subdominio -> remove o 1o rotulo.
      const root = h.replace(/^www\./, "").split(".").slice(1).join(".") || h;
      return `${window.location.protocol}//${root}`;
    }
  }
  return "http://localhost:8080";
}

export const SYSTEMS = {
  portal: portalHost(),
  rh: "http://localhost:4001",
  dp: "http://localhost:4002",
  planejamento: "http://localhost:4005",
  operacional: "http://localhost:4006",
} as const;

/** Lê o usuário vindo do Portal/outro sistema pela URL (#sso=...). */
export function readSsoUser(): string | null {
  const m = window.location.hash.match(/(?:^|[#&])sso=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Remove o token SSO da URL após o login (deixa a barra de endereço limpa). */
export function clearSsoFromUrl() {
  if (window.location.hash.includes("sso=")) {
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

/** Navega para outro sistema levando o usuário logado (login único). */
export function goToSystem(url: string, user: string | null) {
  window.location.href = user ? `${url}/#sso=${encodeURIComponent(user)}${trechoDaSessao()}` : url;
}

/** Identificador deste sistema no Portal (usado na checagem de acesso). */
export const SYSTEM_ID = "colaborador";

/** Login global do Admin Supremo — sempre liberado e sempre admin (sem cadastro local). */
export const ADMIN_SUPREMO_LOGIN = "admin@portalsercamp.com";

/** É o Admin Supremo? (comparação sempre em minúsculas, com trim). */
export function isAdminSupremo(user: string | null | undefined): boolean {
  return (user ?? "").trim().toLowerCase() === ADMIN_SUPREMO_LOGIN;
}

export interface AcessoResult {
  allowed: boolean;
  adminSupremo: boolean;
  /** Acesso somente visualização (cadastro do Portal). */
  somenteLeitura: boolean;
  /** Abas liberadas neste sistema; null = todas. */
  abas: string[] | null;
}

/**
 * Pergunta ao Portal se o usuário tem acesso a este sistema.
 * GET {PORTAL}/api/acesso?u=<usuario>&sistema=<SYSTEM_ID> -> { allowed, adminSupremo, somenteLeitura, abas }.
 * Em caso de falha de rede/erro, lança — o chamador trata de forma segura (não liberar).
 */
export async function checkAcesso(user: string): Promise<AcessoResult> {
  const url = `${SYSTEMS.portal}/api/acesso?u=${encodeURIComponent(user)}&sistema=${encodeURIComponent(SYSTEM_ID)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Falha na verificação de acesso (HTTP ${res.status})`);
  const body = (await res.json()) as Partial<AcessoResult>;
  return {
    allowed: !!body.allowed,
    adminSupremo: !!body.adminSupremo,
    somenteLeitura: body.somenteLeitura === true,
    abas: body.somenteLeitura === true && Array.isArray(body.abas) ? body.abas.map(String) : null,
  };
}
